import { DS_POPOVER_PLACEMENTS, DSPopoverPlacement } from "../types/props";
import { Position } from "@/src/design_system/primitives/popover/types/position";
import { DS_POPOVER_ARROW_SIDE, type DSPopoverArrowSide } from "@/src/design_system/primitives/popover/types/arrow";

/** Keeps the arrow clear of the rounded corners. */
export const ARROW_EDGE_INSET = 20;

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

export const calculatePopoverCoordinates = ({
  activatorRect,
  contentRect,
  placement,
  offset,
}: {
  activatorRect: DOMRect;
  contentRect: DOMRect;
  placement: DSPopoverPlacement;
  offset: number;
}): Position => {
  const positionMap: Record<DSPopoverPlacement, () => Position> = {
    [DS_POPOVER_PLACEMENTS.BottomStart]: () => ({
      top: activatorRect.bottom + offset,
      left: activatorRect.left,
    }),
    [DS_POPOVER_PLACEMENTS.BottomEnd]: () => ({
      top: activatorRect.bottom + offset,
      left: activatorRect.right - contentRect.width,
    }),
    [DS_POPOVER_PLACEMENTS.TopStart]: () => ({
      top: activatorRect.top - contentRect.height - offset,
      left: activatorRect.left,
    }),
    [DS_POPOVER_PLACEMENTS.TopEnd]: () => ({
      top: activatorRect.top - contentRect.height - offset,
      left: activatorRect.right - contentRect.width,
    }),
    [DS_POPOVER_PLACEMENTS.RightStart]: () => ({
      top: activatorRect.top,
      left: activatorRect.right + offset,
    }),
    [DS_POPOVER_PLACEMENTS.LeftStart]: () => ({
      top: activatorRect.top,
      left: activatorRect.left - contentRect.width - offset,
    }),
    [DS_POPOVER_PLACEMENTS.RightEnd]: () => ({
      top: activatorRect.bottom - contentRect.height,
      left: activatorRect.right + offset,
    }),
    [DS_POPOVER_PLACEMENTS.LeftEnd]: () => ({
      top: activatorRect.bottom - contentRect.height,
      left: activatorRect.left - contentRect.width - offset,
    }),
  };

  return positionMap[placement]();
};

export const applyViewportConstraints = ({
  position,
  contentRect,
  viewportWidth,
  viewportHeight,
  padding,
}: {
  position: Position;
  contentRect: DOMRect;
  viewportWidth: number;
  viewportHeight: number;
  padding: number;
}): Position => {
  let { top, left } = position;

  // Horizontal constraints
  if (left < padding) {
    left = padding;
  } else if (left + contentRect.width > viewportWidth - padding) {
    left = viewportWidth - contentRect.width - padding;
  }

  // Vertical constraints
  if (top < padding) {
    top = padding;
  } else if (top + contentRect.height > viewportHeight - padding) {
    top = viewportHeight - contentRect.height - padding;
  }

  return { top, left };
};

/**
 * Position of the arrow along the content edge so that it points at the activator's center.
 * Measured from the content's left edge for top/bottom arrows and from its top edge for left/right arrows.
 */
export const calculateArrowOffset = ({
  activatorRect,
  contentRect,
  position,
  side,
  edgeInset = ARROW_EDGE_INSET,
}: {
  activatorRect: DOMRect;
  contentRect: DOMRect;
  position: Position;
  side: DSPopoverArrowSide;
  edgeInset?: number;
}): number => {
  if (side === DS_POPOVER_ARROW_SIDE.Top || side === DS_POPOVER_ARROW_SIDE.Bottom) {
    const center = activatorRect.left + activatorRect.width / 2 - position.left;
    return clamp(center, edgeInset, Math.max(edgeInset, contentRect.width - edgeInset));
  }

  const center = activatorRect.top + activatorRect.height / 2 - position.top;
  return clamp(center, edgeInset, Math.max(edgeInset, contentRect.height - edgeInset));
};
